import { AbsoluteModelPath, createModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { IUpdateFormModelAccessor } from "../../../../Engine/Form/IFormModelAccessor";
import { ViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { isNotNullOrEmpty, isNotNullOrUndefined, isNullOrEmpty } from "../../../../../../Common/TypingUtils";
import { FiasExtendFieldsType, ParsedExtendedFiasField } from "../FiasInput/Types/FiasTypes";
import { FiasApi } from "../FiasInput/FiasApi";
import { parseFullAddressFromRequest } from "../FiasInput/Utils/utils";
import { ModelHunk } from "../../../../Engine/Core/ModelStoring/ModelHunk";
import { FiasContainerProps, FnsOptions } from "../FiasContainer";
import { IterUtils } from "../../../../../../Common/IterableUtils";
import { acceptHelperConvertationToShow } from "../FiasInput/Utils/Convertations/acceptConvertationHelper";
import { OptionalComponentIdExtractor } from "../../../Services/ComponentIdExtraction";
import { isValidGuid } from "../../../../../../Common/UUID";
import { FiasDivision } from "../FiasEditor/FiasWidget/FiasWidgetTypes";

import { convertFiasAddressToModelAddress } from "./FiasConversions";

export interface FiasAddress {
    [key: string]: string;
}

// Нужен, чтобы не делать каждый раз запрос на сервак за парсингом гуидов из useEffect + отправление метрики
// eslint-disable-next-line @typescript-eslint/no-unsafe-return
export const dirtyReduxHack = (fiasValue: Object, extraOptions: Object = {}): string[] => [
    ...Object.values(fiasValue),
    ...Object.values(extraOptions),
];

export const fiasWarningMessage = "Не сохранится в отчет, выберите адрес из справочника";
export const fiasUnknownGuidMessage = "Не удалось определить адрес по уникальному идентификатору - Guid";

export const fiasInvalidGuidMessage = "Значение должно быть уникальным идентификатором (Guid)";

export const fiasNoMatchAddressByGuidMessage = "Значение не соответствует выбранному адресу";

export const fiasHasOnlyNotRequiredPathFields = "Не заполнены обязательные поля адреса, выберите адрес из справочника";

export function setFiasAddressToModel(
    model: IUpdateFormModelAccessor,
    nextValue: FiasAddress,
    { bindingPath, pathToFullAddress, childrenOptions, extendedFields, fnsOptions }: FiasContainerProps
): void {
    const result: ModelHunk = new ModelHunk();
    if (childrenOptions) {
        const currentStacksCount = getCurrentStacksCount(extendedFields, nextValue);
        model.setChildren(bindingPath.joinWith(childrenOptions.path), [
            ...Iterator.from(IterUtils.range(currentStacksCount)).map(x => x.toString()),
        ]);
    }
    if (isNotNullOrEmpty(pathToFullAddress)) {
        result.set(createModelPath(bindingPath.toLegacyPath() + "/" + pathToFullAddress, true), {
            value: nextValue[pathToFullAddress],
        });
    }
    const emptyValue = getEmptyValueForModel(fnsOptions);
    for (const [path, value] of Object.entries(nextValue)) {
        result.set(createModelPath(bindingPath.toLegacyPath() + "/" + path, true), {
            // @ts-ignore
            value: value ? value : emptyValue,
        });
    }
    model.setBatch(result);
}

function getEmptyValueForModel(fnsOptions?: FnsOptions) {
    return fnsOptions ? null : "";
}

function getCurrentStacksCount(extendedFields: ParsedExtendedFiasField[], nextValue: FiasAddress): number {
    const children = extendedFields
        .map(field => {
            if (!field.child) {
                return undefined;
            }

            const modelPath = createModelPath(field.path, true);
            const index = modelPath.getFirstMultiplicityInstanceNumber();

            if (index === -1) {
                return undefined;
            }

            return { field: field, index: index };
        })
        .filter(isNotNullOrUndefined);

    const childrenGroupedByIndex = IterUtils.groupBy(children, child => child.index);

    let maxNotNullableIndex = -1;

    childrenGroupedByIndex.forEach(groupedChildren => {
        const notNullableChildren = groupedChildren.find(child => isNotNullOrEmpty(nextValue[child.field.path]));

        if (notNullableChildren) {
            maxNotNullableIndex = Math.max(maxNotNullableIndex, notNullableChildren.index);
        }
    });

    return maxNotNullableIndex + 1;
}

export async function extractStringAddress(
    address: FiasAddress,
    extendedFields: ParsedExtendedFiasField[],
    apiClient: FiasApi
): Promise<string> {
    const stringAddress = await getStringAddressByPath(address, extendedFields, apiClient);
    return stringAddress ?? "";
}

async function getStringAddressByPath(
    address: FiasAddress,
    extendedFields: ParsedExtendedFiasField[],
    apiClient: FiasApi
) {
    const promises = await Promise.all(
        extendedFields
            .reduce<{ repeatsPath: string[]; result: ParsedExtendedFiasField[] }>(
                (acc, field) => {
                    if (acc.repeatsPath.includes(field.path)) {
                        return acc;
                    }
                    acc.repeatsPath.push(field.path);
                    acc.result.push(field);
                    return acc;
                },
                { repeatsPath: [], result: [] }
            )
            .result.map(async field => extractAddressFromPathField(address, field, apiClient))
    );
    const resultAddress = promises.filter(isNotNullOrEmpty).join(" ");
    return formatStringAddress(resultAddress);
}

const formatStringAddress = (address: string) =>
    address
        .replace(/((, )|(,))+$/, "")
        .replace(new RegExp(`((${FiasDivision.Municipal})|(${FiasDivision.Administrative}))`, "g"), "")
        .trim();

async function extractAddressFromPathField(address: FiasAddress, field: ParsedExtendedFiasField, apiClient: FiasApi) {
    let partAddressElement = address[field.path];
    if (!checkDisplayAddressExists(field.display, partAddressElement)) {
        return undefined;
    }
    if (checkGuidType(field) && isNotNullOrEmpty(partAddressElement)) {
        const parsedGuid = await parseGuidToTextAddress(partAddressElement, apiClient);
        return parsedGuid;
    } else {
        if (isNotNullOrEmpty(field.convertToShow) && typeof partAddressElement === "string") {
            partAddressElement = await acceptHelperConvertationToShow(
                partAddressElement,
                field.convertToShow,
                apiClient
            );
        }
        if (isNotNullOrEmpty(field.prefix) && partAddressElement) {
            partAddressElement = addPrefix(partAddressElement, field.prefix);
        }
        if (field.endComma) {
            partAddressElement = addCommaToEnd(partAddressElement);
        }
        if (field.clearSubstring && partAddressElement) {
            partAddressElement = partAddressElement.replaceAll(field.clearSubstring, "");
        }
        return partAddressElement;
    }
}

function addCommaToEnd(partAddressElement?: string): string {
    return `${partAddressElement},`;
}

function addPrefix(partAddressElement: string, prefix: string): string {
    return `${prefix}${partAddressElement}`;
}

export function checkDisplayAddressExists(isDisplay: boolean, partAddressElement: string | undefined): boolean {
    return isDisplay && isNotNullOrEmpty(partAddressElement);
}

function checkGuidType(field: ParsedExtendedFiasField): boolean {
    return field.type === FiasExtendFieldsType.guid;
}

async function parseGuidToTextAddress(guid: string, apiClient: FiasApi): Promise<string> {
    if (isValidGuid(guid)) {
        const fiasAddressFields = await apiClient.fetchFullAddressesByFiasId(guid);

        const parsedStringAddress = parseFullAddressFromRequest(fiasAddressFields);
        return parsedStringAddress;
    }
    throw new Error("Invalid Guid format");
}

export async function getModelAddressByGuid(
    guid: string,
    extendedFields: ParsedExtendedFiasField[],
    apiClient: FiasApi
) {
    if (isValidGuid(guid)) {
        const fiasAddressFields = await apiClient.fetchFullAddressesByFiasId(guid);
        const guidAddress = convertFiasAddressToModelAddress(fiasAddressFields, extendedFields);
        return guidAddress;
    }
    throw new Error("Invalid Guid format");
}

async function getStringAddressByGuid(guid: string, extendedFields: ParsedExtendedFiasField[], apiClient: FiasApi) {
    if (isValidGuid(guid)) {
        const guidAddress = await getModelAddressByGuid(guid, extendedFields, apiClient);
        const guidStringAddress = await getStringAddressByPath(guidAddress, extendedFields, apiClient);
        return guidStringAddress;
    }
    throw new Error("Invalid Guid format");
}

export function extractFssAddressFromBindingPath(
    viewModelSelect: ViewModelSelect,
    state: FullFormState,
    bindingPath: AbsoluteModelPath,
    extendedFields: ParsedExtendedFiasField[]
): FiasAddress {
    return extendedFields.reduce((acc, field) => {
        acc[field.path] = viewModelSelect.pathViewModel(state, bindingPath.joinWith(...field.path.split("/")))
            .value as string;
        return acc;
    }, {} as FiasAddress);
}

export function isAnyAddressByPathExists(address: FiasAddress, extendedFields: ParsedExtendedFiasField[]): boolean {
    return extendedFields.some(field => {
        const clearSubstring = field.clearSubstring ? address[field.path]?.replaceAll(field.clearSubstring, "") : true;
        const withAutoValue = field.sourceValue.filter(source => source.autoValue).length === 0 ? true : false;
        return !!(clearSubstring && field.display && withAutoValue && isNotNullOrEmpty(address[field.path]));
    });
}

export function getFiasControlModel(
    viewModelSelect: ViewModelSelect,
    state: FullFormState,
    bindingPath: AbsoluteModelPath,
    extendedFields: ParsedExtendedFiasField[]
): { error: string; warning: string } {
    const controlModel = extendedFields.reduce(
        (acc, field) => {
            const modelValue = viewModelSelect.pathViewModel(state, bindingPath.joinWith(...field.path.split("/")));
            acc.error = isNullOrEmpty(acc.error) ? (modelValue.error?.[0] ?? "") : acc.error;
            acc.warning = isNullOrEmpty(acc.warning) ? (modelValue.warning?.[0] ?? "") : acc.warning;
            return acc;
        },
        { error: "", warning: "" }
    );
    return controlModel;
}

export function fiasComponentIdExtractor(): OptionalComponentIdExtractor<FiasContainerProps> {
    return function* extractFiasComponentIds(props: FiasContainerProps): Iterable<string> {
        yield props.bindingPath.toLegacyPath().toString();

        for (const field of props.extendedFields) {
            yield props.bindingPath
                .joinWith(...field.path.split("/"))
                .toLegacyPath()
                .toString();
        }
    };
}

export async function getGuidAddressValidationResult(
    guid: string,
    address: FiasAddress,
    extendedFields: ParsedExtendedFiasField[],
    guidAddressField: string,
    apiClient: FiasApi
): Promise<{ error: string; warning: string }> {
    if (isNullOrEmpty(guid)) {
        return { error: "empty", warning: "" };
    }
    if (!isValidGuid(guid)) {
        return { error: fiasInvalidGuidMessage, warning: fiasInvalidGuidMessage };
    }

    const woGuidAddressFields = extendedFields.filter(field => field.path !== guidAddressField);
    try {
        const guidStringAddress = await getStringAddressByGuid(guid, woGuidAddressFields, apiClient);
        if (isNullOrEmpty(guidStringAddress)) {
            return { error: fiasUnknownGuidMessage, warning: fiasUnknownGuidMessage };
        }

        const woGuidStringAddress = await getStringAddressByPath(address, woGuidAddressFields, apiClient);

        return isNotNullOrEmpty(woGuidStringAddress) && woGuidStringAddress !== guidStringAddress
            ? { error: "", warning: fiasNoMatchAddressByGuidMessage }
            : { error: "", warning: "" };
    } catch {
        return { error: fiasUnknownGuidMessage, warning: fiasUnknownGuidMessage };
    }
}

export async function checkAddressExists(
    address: string,
    extendedFields: ParsedExtendedFiasField[],
    requiredExtendedFields: ParsedExtendedFiasField[],
    apiClient: FiasApi
): Promise<boolean> {
    const [suggest] = await apiClient.fetchSuggestAddressesByText(address);
    if (suggest) {
        const suggestValue = convertFiasAddressToModelAddress(suggest, extendedFields);
        const suggestAddress = await extractStringAddress(suggestValue, requiredExtendedFields, apiClient);
        return suggestAddress === address;
    }
    return false;
}
